import { geoAlbersUsa, geoContains, geoMercator, geoPath } from 'd3-geo';
import { feature, merge } from 'topojson-client';
import countiesTopology from 'us-atlas/counties-10m.json';

const NON_CONTIGUOUS_STATE_FIPS = new Set(['02', '15', '60', '66', '69', '72', '78']);
const NON_STATE_TERRITORY_FIPS = new Set(['60', '66', '69', '72', '78']);

function normalizeFips(value, width) {
  const digits = String(value ?? '').replace(/\D+/g, '');
  return digits ? digits.padStart(width, '0') : null;
}

const countyFeatures = feature(
  countiesTopology,
  countiesTopology.objects.counties,
).features.map((shape) => ({
  ...shape,
  normalizedId: normalizeFips(shape.id, 5),
}));

const stateFeatures = feature(
  countiesTopology,
  countiesTopology.objects.states,
).features.map((shape) => ({
  ...shape,
  normalizedId: normalizeFips(shape.id, 2),
}));

const contiguousCountyFeatures = countyFeatures.filter((shape) => (
  shape.normalizedId && !NON_CONTIGUOUS_STATE_FIPS.has(shape.normalizedId.slice(0, 2))
));

const contiguousStateFeatures = stateFeatures.filter((shape) => (
  shape.normalizedId && !NON_CONTIGUOUS_STATE_FIPS.has(shape.normalizedId)
));

const usStateFeatures = stateFeatures.filter((shape) => (
  shape.normalizedId && !NON_STATE_TERRITORY_FIPS.has(shape.normalizedId)
));
const contiguousGeometryCache = new Map();
const stateGeometryCache = new Map();
const stateCountyGeometryCache = new Map();

function featureCollection(features) {
  return { type: 'FeatureCollection', features };
}

export function findContiguousUsCountyFips(longitude, latitude) {
  if (longitude == null || latitude == null || longitude === '' || latitude === '') return null;
  const coordinate = [Number(longitude), Number(latitude)];
  if (!coordinate.every(Number.isFinite)) return null;
  return contiguousCountyFeatures.find((shape) => geoContains(shape, coordinate))?.normalizedId || null;
}

export function buildContiguousUsCountyGeometry({
  width = 1240,
  height = 580,
  padding = 6,
} = {}) {
  const cacheKey = `${width}:${height}:${padding}`;
  if (contiguousGeometryCache.has(cacheKey)) return contiguousGeometryCache.get(cacheKey);
  const projection = geoAlbersUsa().fitExtent(
    [[padding, padding], [width - padding, height - padding]],
    featureCollection(contiguousCountyFeatures),
  );
  const path = geoPath(projection);
  const geometry = {
    projection,
    counties: contiguousCountyFeatures.map((shape) => ({
      geoId: shape.normalizedId,
      d: path(shape),
      centroid: path.centroid(shape),
    })),
    states: contiguousStateFeatures.map((shape) => ({
      geoId: shape.normalizedId,
      d: path(shape),
    })),
  };
  contiguousGeometryCache.set(cacheKey, geometry);
  return geometry;
}

export function buildStateCountyGeometry(stateFips, {
  width = 1240,
  height = 730,
  padding = 12,
} = {}) {
  const normalizedStateFips = normalizeFips(stateFips, 2);
  if (!normalizedStateFips || NON_CONTIGUOUS_STATE_FIPS.has(normalizedStateFips)) return null;
  const cacheKey = `${normalizedStateFips}:${width}:${height}:${padding}`;
  if (stateCountyGeometryCache.has(cacheKey)) return stateCountyGeometryCache.get(cacheKey);

  const counties = contiguousCountyFeatures.filter((shape) => (
    shape.normalizedId.slice(0, 2) === normalizedStateFips
  ));
  const state = contiguousStateFeatures.find((shape) => shape.normalizedId === normalizedStateFips);
  if (!counties.length || !state) return null;

  const projection = geoMercator().fitExtent(
    [[padding, padding], [width - padding, height - padding]],
    state,
  );
  const path = geoPath(projection);
  const geometry = {
    projection,
    counties: counties.map((shape) => ({
      geoId: shape.normalizedId,
      d: path(shape),
      centroid: path.centroid(shape),
    })),
    states: [{ geoId: state.normalizedId, d: path(state) }],
  };
  stateCountyGeometryCache.set(cacheKey, geometry);
  return geometry;
}

export function buildRegionZctaGeometry(countyFipsValues, zctaFeatureCollection, {
  width = 1240,
  height = 730,
  padding = 18,
} = {}) {
  const countyFips = new Set((countyFipsValues || [])
    .map((value) => normalizeFips(value, 5))
    .filter(Boolean));
  if (!countyFips.size) return null;

  const countyTopologyGeometries = countiesTopology.objects.counties.geometries.filter((shape) => (
    countyFips.has(normalizeFips(shape.id, 5))
  ));
  const counties = contiguousCountyFeatures.filter((shape) => countyFips.has(shape.normalizedId));
  if (!countyTopologyGeometries.length || !counties.length) return null;

  const regionGeometry = merge(countiesTopology, countyTopologyGeometries);
  const regionFeature = { type: 'Feature', properties: {}, geometry: regionGeometry };
  const projection = geoMercator().fitExtent(
    [[padding, padding], [width - padding, height - padding]],
    regionFeature,
  );
  const path = geoPath(projection);
  const sourceZctas = Array.isArray(zctaFeatureCollection?.features)
    ? zctaFeatureCollection.features
    : [];
  // The region endpoint already assigns ZIP areas against full-precision
  // Census county boundaries; only the state-wide fallback needs this filter.
  const serverMembership = zctaFeatureCollection?.region_membership === 'server';
  const zctas = sourceZctas.filter((shape) => {
    if (serverMembership) return true;
    const longitude = Number(shape?.properties?.centroid_longitude);
    const latitude = Number(shape?.properties?.centroid_latitude);
    return Number.isFinite(longitude)
      && Number.isFinite(latitude)
      && geoContains(regionFeature, [longitude, latitude]);
  }).map((shape) => {
    const longitude = Number(shape.properties.centroid_longitude);
    const latitude = Number(shape.properties.centroid_latitude);
    return {
      geoId: String(shape.properties.zcta5 || shape.id || ''),
      d: path(shape),
      centroid: projection([longitude, latitude]),
      feature: shape,
      properties: shape.properties || {},
    };
  });

  return {
    projection,
    counties: counties.map((shape) => ({
      geoId: shape.normalizedId,
      d: path(shape),
      centroid: path.centroid(shape),
    })),
    states: [{ geoId: 'region-outline', d: path(regionFeature) }],
    zctas,
    regionFeature,
  };
}

export function buildUsStateGeometry({
  width = 1240,
  height = 730,
  padding = 18,
} = {}) {
  const cacheKey = `${width}:${height}:${padding}`;
  if (stateGeometryCache.has(cacheKey)) return stateGeometryCache.get(cacheKey);
  const projection = geoAlbersUsa().fitExtent(
    [[padding, padding], [width - padding, height - padding]],
    featureCollection(usStateFeatures),
  );
  const path = geoPath(projection);
  const geometry = usStateFeatures.map((shape) => ({
    geoId: shape.normalizedId,
    d: path(shape),
  }));
  stateGeometryCache.set(cacheKey, geometry);
  return geometry;
}
